/**
 * O levantamento técnico, preenchido no local.
 *
 * A checklist vem do servidor conforme a categoria da obra — pintura pede
 * área e demãos, canalização pede pressão e fugas. Vive lá e não aqui para
 * que acrescentar uma categoria não obrigue a publicar a app outra vez.
 *
 * Não há um único campo de preço, de propósito. O avaliador mede e conta; o
 * valor comercial é decidido depois, por quem concorre. É também por isso que
 * ele fica impedido de propor a esta obra assim que entrega o relatório — e o
 * ecrã diz-lho antes de ele carregar no botão, em vez de o descobrir depois.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Camera, CheckCircle2, AlertTriangle, MapPin } from 'lucide-react';
import api from '../../utils/api';

const SurveyReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [obra, setObra] = useState(null);
  const [campos, setCampos] = useState([]);
  const [valores, setValores] = useState({});
  const [observacoes, setObservacoes] = useState('');
  const [fotos, setFotos] = useState([]);
  const [erro, setErro] = useState('');
  const [busy, setBusy] = useState(false);
  const [feito, setFeito] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/survey/lista de verificação`),
      ]);
      setObra(p.data);
      setCampos(c.data.campos || []);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível abrir esta visita.');
    }
  }, [id]);
  useEffect(() => { carregar(); }, [carregar]);

  const pôr = (campoId, v) => setValores((x) => ({ ...x, [campoId]: v }));

  const emFalta = campos.filter((c) => c.obrigatorio && (valores[c.id] === undefined || valores[c.id] === ''));

  const submeter = async () => {
    if (emFalta.length) { setErro(`Falta preencher: ${emFalta.map((c) => c.rotulo).join(', ')}`); return; }
    setBusy(true); setErro('');
    try {
      // As fotografias primeiro: se o relatório passasse e as fotos
      // falhassem, o levantamento ficava fechado sem prova nenhuma.
      if (fotos.length) {
        const fd = new FormData();
        fotos.forEach((f) => fd.append('ficheiros', f));
        fd.append('tipo', 'foto');
        fd.append('descricao', 'Vistoria técnica');
        await api.post(`/projects/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await api.post(`/projects/${id}/survey/report`, { relatorio: valores, observacoes });
      setFeito(true);
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível entregar o relatório.');
    } finally { setBusy(false); }
  };

  if (erro && !obra) {
    return <p className="text-red-400 text-sm text-center py-16 px-6">{erro}</p>;
  }
  if (!obra) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  if (feito) {
    return (
      <div className="text-center py-12 space-y-4">
        <CheckCircle2 className="w-16 h-16 mx-auto text-green-400" />
        <p className="font-semibold text-green-400">Vistoria entregue.</p>
        <p className="text-sm text-slate-400 max-w-sm mx-auto">
          A gestão vai preparar o caderno de encargos a partir das suas medições.
        </p>
        <button onClick={() => navigate('/admin')}
          className="px-5 py-3 rounded-xl bg-slate-800 text-slate-200 font-semibold">
          Voltar às visitas
        </button>
      </div>
    );
  }

  const p = obra.projecto;

  return (
    <div className="space-y-4 pb-28">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/admin')} aria-label="Voltar"
          className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-slate-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold leading-tight truncate">{p.titulo || p.categoria}</h1>
          <p className="text-xs text-slate-500 font-mono">{p.codigo} · {p.categoria}</p>
        </div>
      </div>

      {p.localizacao && (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <MapPin className="w-4 h-4 shrink-0" /> {p.localizacao}
        </div>
      )}

      <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">O que o cliente pediu</div>
        <p className="text-sm text-slate-300 whitespace-pre-wrap">{p.descricao}</p>
      </div>

      <ol className="space-y-3">
        {campos.map((c) => (
          <li key={c.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800">
            <label htmlFor={`c-${c.id}`} className="block font-medium text-slate-100 mb-2">
              {c.rotulo}
              {c.obrigatorio && <span className="text-red-400 ml-1">*</span>}
            </label>

            {c.tipo === 'numero' && (
              <input id={`c-${c.id}`} type="number" inputMode="decimal" step="any"
                value={valores[c.id] ?? ''} onChange={(e) => pôr(c.id, e.target.value)}
                className="w-full min-h-[48px] px-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100 text-lg" />
            )}

            {c.tipo === 'texto' && (
              <input id={`c-${c.id}`} value={valores[c.id] ?? ''} onChange={(e) => pôr(c.id, e.target.value)}
                className="w-full min-h-[48px] px-3 rounded-lg bg-slate-950 border border-slate-800 text-slate-100" />
            )}

            {c.tipo === 'sim_nao' && (
              <div className="flex gap-2">
                {[['Sim', true], ['Não', false]].map(([rot, v]) => (
                  <button key={rot} type="button" onClick={() => pôr(c.id, v)}
                    aria-pressed={valores[c.id] === v}
                    className={`flex-1 min-h-[48px] rounded-lg font-medium border
                      ${valores[c.id] === v
                        ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/50'
                        : 'bg-slate-950 text-slate-400 border-slate-800'}`}>
                    {rot}
                  </button>
                ))}
              </div>
            )}

            {c.tipo === 'opcoes' && (
              <div className="grid gap-2">
                {c.opcoes.map((o) => (
                  <button key={o} type="button" onClick={() => pôr(c.id, o)}
                    aria-pressed={valores[c.id] === o}
                    className={`min-h-[48px] px-3 rounded-lg text-left font-medium border
                      ${valores[c.id] === o
                        ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/50'
                        : 'bg-slate-950 text-slate-400 border-slate-800'}`}>
                    {o}
                  </button>
                ))}
              </div>
            )}

            {c.tipo === 'multi' && (
              <div className="flex flex-wrap gap-2">
                {c.opcoes.map((o) => {
                  const sel = (valores[c.id] || []).includes(o);
                  return (
                    <button key={o} type="button" aria-pressed={sel}
                      onClick={() => pôr(c.id, sel
                        ? (valores[c.id] || []).filter((x) => x !== o)
                        : [...(valores[c.id] || []), o])}
                      className={`min-h-[44px] px-4 rounded-full text-sm font-medium border
                        ${sel ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/50'
                              : 'bg-slate-950 text-slate-400 border-slate-800'}`}>
                      {o}
                    </button>
                  );
                })}
              </div>
            )}
          </li>
        ))}
      </ol>

      <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
        <label className="block font-medium text-slate-100 mb-2">Fotografias técnicas</label>
        <input type="file" multiple accept="image/*" capture="environment"
          onChange={(e) => setFotos([...e.target.files].slice(0, 8))}
          className="w-full text-sm text-slate-400" />
        {fotos.length > 0 && (
          <p className="text-xs text-cyan-300 mt-2 inline-flex items-center gap-1">
            <Camera className="w-3.5 h-3.5" /> {fotos.length} fotografia{fotos.length > 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="obs" className="block font-medium text-slate-100 mb-2">Observações</label>
        <textarea id="obs" rows={4} value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
          placeholder="O que a lista de verificação não cobre: acessos, condicionantes, riscos."
          className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-200" />
      </div>

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      {/* Dizer o efeito ANTES do botão. Descobrir depois que se ficou
          impedido de concorrer seria uma surpresa desagradável. */}
      <div className="flex gap-2 p-3 rounded-xl bg-amber-500/10 text-amber-200 text-sm">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <span>Ao entregar, fica impedido de apresentar proposta a esta obra. Quem avalia não executa.</span>
      </div>

      {/* Acima da barra de navegação, não em cima dela. As duas eram "fixed
          bottom-0" e a navegação, desenhada depois, tapava este botão por
          inteiro: o avaliador preenchia o levantamento todo e não tinha como
          o entregar. */}
      <div className="fixed bottom-[calc(4.25rem+env(safe-area-inset-bottom))] inset-x-0 z-30 p-3 bg-slate-950 border-t border-slate-800">
        <button onClick={submeter} disabled={busy}
          className="w-full min-h-[52px] rounded-xl bg-cyan-600 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {emFalta.length
            ? (emFalta.length === 1 ? 'Falta 1 campo' : `Faltam ${emFalta.length} campos`)
            : 'Concluir vistoria'}
        </button>
      </div>
    </div>
  );
};

export default SurveyReport;
